import { Component, computed, effect, HostListener, inject, input, OnInit, output, signal } from '@angular/core';
import { form, minLength, required, FormField } from '@angular/forms/signals';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { ImageService } from '../../../../services/image/image.service';
import { ImageTypesEnum, S3BucketEnum } from '../../../../common/general-types/media-assets.dto';
import { S3uploaderService } from '../../../../services/s3uploader/s3uploader.service';
import { ProductService } from '../../../../services/product/product.service';
import { AppDetailsDto, CreateProductDto, PlayAppDetailsDto, UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { Subject, take, firstValueFrom, distinctUntilChanged, debounceTime } from 'rxjs';
import { AddImageComponent } from '../../../../atoms/add-image/add-image.component';
import { FormsModule } from '@angular/forms';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { MatCalendarCellClassFunction, MatDatepickerModule } from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import { ViewportOverlayDirective } from '../../../../common/directives/viewport-overlay/viewport-overlay.directive';

@Component({
  selector: 'mb-brand-products-create',
  imports: [FormField, AddImageComponent, FormsModule, MatDatepickerModule, MatFormFieldModule, MatInputModule],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './brand-products-create.component.html',
  styleUrl: './brand-products-create.component.css'
})
export class BrandProductsCreateComponent implements OnInit {
  selectedProduct = input<UgcProductResponseDto | undefined>(undefined);

  close = output<boolean>();
  productAdded = output<UgcProductResponseDto>();
  productAmended = output<UgcProductResponseDto>();
  productRemoved = output<unknown>();

  /** Edit mode for list-backed products (physical, digital, event — not mobile app). */
  isEditMode = computed(() => {
    const p = this.selectedProduct();
    return p !== undefined && p.productType !== 2;
  });

  private lastHydratedProductId: string | null = null;

  selectedProductType = signal<number>(0);

  currencies: { code: string, symbol: string }[] = [
    { code: 'GBP', symbol: '£' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
  ];

  /** ISO currency + symbol for product forms, inferred from brand default billing currency. */
  private currencyDisplayFromBrand(brandCurrency: string | undefined): { code: string; symbol: string } {
    switch (brandCurrency?.toLowerCase()) {
      case 'gbp':
        return { code: 'GBP', symbol: '£' };
      case 'eur':
        return { code: 'EUR', symbol: '€' };
      case 'usd':
      default:
        return { code: 'USD', symbol: '$' };
    }
  }

  /** Selected Product Photos */
  selectedFiles: any[] = [];
  productImage = signal<any>('');
  imageLoading = signal<boolean>(false);
  isDragging = signal<boolean>(false);

  S3Bucket = S3BucketEnum;
  ImageTypes = ImageTypesEnum;

  /** Loading states — granular so the template can show per-stage feedback */
  findingProductDetailsScrape = signal<boolean>(false);
  findingProductDetailsOpenAI = signal<boolean>(false);
  savingProduct = signal<boolean>(false);

  /** URL debounce subjects */
  physicalProductUrlUpdate = new Subject<string>();
  eventUrlUpdate = new Subject<string>();

  private alertService = inject(AlertService);
  private toastNotificationService = inject(ToastNotificationService);
  private currentUserService = inject(CurrentUserService);
  public imageService = inject(ImageService);
  private uploadService = inject(S3uploaderService);
  public productService = inject(ProductService);
  private firebaseService = inject(FirebaseService);

  selectedCurrency = signal<{ code: string; symbol: string } | undefined>(
    this.currencyDisplayFromBrand(this.currentUserService.focussedBrand.currencyCode),
  );

  /** Forms */
  physicalProductModel = signal({
    productCategory: '',
    productIndustry: '',
    productName: '',
    productDescription: '',
    website: '',
    accessInstructions: '',
    productValue: 0,
    currency: this.currencyDisplayFromBrand(this.currentUserService.focussedBrand.currencyCode).code,
  })
  physicalProducForm = form(this.physicalProductModel, (schemaPath) => {
    required(schemaPath.productName, { message: 'Product name is required' });
    minLength(schemaPath.productName, 2, { message: 'Product name must be at least 2 characters long' });
    required(schemaPath.productDescription, { message: 'Product description is required' });
    minLength(schemaPath.productDescription, 2, { message: 'Product description must be at least 2 characters long' });
  })

  eventTicketsModel = signal({
    productName: '',
    productDescription: '',
    website: '',
    location: '',
    dateType: 1,
    date: new Date(),
    dateDescription: '',
    productValue: 0,
    currency: this.currencyDisplayFromBrand(this.currentUserService.focussedBrand.currencyCode).code,
  })
  eventTicketsForm = form(this.eventTicketsModel, (schemaPath) => {
    required(schemaPath.productName, { message: 'Event title is required' });
    required(schemaPath.productDescription, { message: 'Event description is required' });
    required(schemaPath.location, { message: 'Event location is required' });
    required(schemaPath.dateType, { message: 'Event date type is required' });
    required(schemaPath.date, { message: 'Event date is required' });
    required(schemaPath.dateDescription, { message: 'Event date description is required' });
    required(schemaPath.productValue, { message: 'Event price is required' });
    required(schemaPath.currency as any, { message: 'Event currency is required' });
  })
  /** Forms END */

  /** Mobile Apps */
  appStoreQuery: string | undefined;
  appleSearchUpdate = new Subject<string>();
  appleSuggestions = signal<AppDetailsDto[] | undefined>(undefined);
  playSuggestions = signal<PlayAppDetailsDto[] | undefined>(undefined);
  appStoreSearchLoading = signal<boolean>(false);

  selectedAndroid = signal<PlayAppDetailsDto | undefined>(undefined);
  selectedApple = signal<AppDetailsDto | undefined>(undefined);
  showFullTextAndroid = signal<boolean>(false);
  showFullTextIos = signal<boolean>(false);
  /** Mobile Apps - END */

  /** Event Tickets */
  eventTicketsDate = signal<Date | undefined>(undefined);
  showDateSelect = signal<boolean>(false);
  today: Date = new Date();
  selectedDate = signal<Date | undefined>(undefined);
  /** Event Tickets - END */

  constructor() {
    effect(() => {
      const p = this.selectedProduct();
      if (!p?.id) {
        this.lastHydratedProductId = null;
        return;
      }
      if (this.lastHydratedProductId === p.id) {
        return;
      }
      this.lastHydratedProductId = p.id;
      this.hydrateFromProduct(p);
    });
  }

  @HostListener('document:keydown.escape', ['$event' as any])
  handleEscapeKey(event: KeyboardEvent) {
    this.closePanel();
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    return '';
  };

  ngOnInit(): void {
    // App store search debounce (existing)
    this.appleSearchUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged())
      .subscribe(() => {
        this.appStoreSearch();
        this.playStoreSearch();
      });

    // Auto-detect product details when physical/digital URL changes
    this.physicalProductUrlUpdate.pipe(
      debounceTime(700),
      distinctUntilChanged(),
    ).subscribe(url => {
      if (this.isValidUrl(url)) {
        this.findProductDetails(url);
      }
    });

    // Auto-detect event details when event URL changes
    this.eventUrlUpdate.pipe(
      debounceTime(700),
      distinctUntilChanged(),
    ).subscribe(url => {
      if (this.isValidUrl(url)) {
        this.findEventDetails(url);
      }
    });
  }

  whichForm(selectedProductType: number) {
    switch (selectedProductType) {
      case 0:
        return 0;
      case 1:
        return 0;
      default:
        return selectedProductType;
    }
  }

  setProductType(productTypeId: number): void {
    if (this.isEditMode()) {
      return;
    }
    this.selectedProductType.set(productTypeId);
  }

  private parseProductDate(p: UgcProductResponseDto): Date {
    const raw = p.date;
    if (!raw) {
      return new Date();
    }
    if (raw instanceof Date) {
      return raw;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }

  private hydrateFromProduct(p: UgcProductResponseDto): void {
    const code = (p.currency ?? 'GBP').toUpperCase();
    const currencyEntry = this.currencies.find((c) => c.code === code);
    this.selectedCurrency.set({
      code,
      symbol: currencyEntry?.symbol ?? '£',
    });

    this.selectedProductType.set(p.productType);
    this.productImage.set(p.productImage ?? '');

    if (p.productType === 0 || p.productType === 1 || p.productType === 4) {
      this.physicalProductModel.set({
        productCategory: p.productCategory ?? '',
        productIndustry: p.productIndustry ?? '',
        productName: p.productName,
        productDescription: p.productDescription,
        website: p.website ?? '',
        accessInstructions: p.accessInstructions ?? '',
        productValue: (p.productValue ?? 0) / 100,
        currency: code,
      });
    } else if (p.productType === 3) {
      const d = this.parseProductDate(p);
      this.eventTicketsDate.set(d);
      this.selectedDate.set(d);
      this.eventTicketsModel.set({
        productName: p.productName,
        productDescription: p.productDescription,
        website: p.website ?? '',
        location: p.location?.trim() ? p.location : '—',
        dateType: p.dateType ?? 1,
        date: d,
        dateDescription: p.dateDescription ?? '',
        productValue: (p.productValue ?? 0) / 100,
        currency: code,
      });
    }
  }

  private buildPhysicalUgcPayload(): UgcProductResponseDto {
    const existing = this.selectedProduct();
    if (!existing) {
      throw new Error('buildPhysicalUgcPayload: missing selectedProduct');
    }
    const website = this.normalizeOptionalWebsite(this.physicalProductModel().website);
    return {
      ...existing,
      productType: this.selectedProductType(),
      productImage: String(this.productImage()),
      productName: this.physicalProductModel().productName,
      productDescription: this.physicalProductModel().productDescription,
      productValue: Math.round(this.physicalProductModel().productValue * 100),
      currency: this.physicalProductModel().currency,
      website,
      accessInstructions: this.physicalProductModel().accessInstructions,
      productCategory: this.physicalProductModel().productCategory,
      productIndustry: this.physicalProductModel().productIndustry,
    };
  }

  private buildEventUgcPayload(): UgcProductResponseDto {
    const existing = this.selectedProduct();
    if (!existing) {
      throw new Error('buildEventUgcPayload: missing selectedProduct');
    }
    const website = this.normalizeOptionalWebsite(this.eventTicketsModel().website);
    return {
      ...existing,
      productType: 3,
      productImage: String(this.productImage()),
      productName: this.eventTicketsModel().productName,
      productDescription: this.eventTicketsModel().productDescription,
      productValue: Math.round(Number(this.eventTicketsModel().productValue) * 100),
      currency: this.eventTicketsModel().currency,
      website,
      accessInstructions: '',
      dateType: this.eventTicketsModel().dateType as 0 | 1,
      date: this.eventTicketsModel().date ?? undefined,
      dateDescription: this.eventTicketsModel().dateDescription || '',
      location: this.eventTicketsModel().location,
    };
  }

  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /** Called on (input) from the physical/digital URL field */
  onPhysicalUrlInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (!raw) {
      this.physicalProductUrlUpdate.next('');
      return;
    }
    const url = raw.startsWith('http') ? raw : `https://${raw}`;
    this.physicalProductUrlUpdate.next(url);
  }

  /** Called on (input) from the event URL field */
  onEventUrlInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (!raw) {
      this.eventUrlUpdate.next('');
      return;
    }
    const url = raw.startsWith('http') ? raw : `https://${raw}`;
    this.eventUrlUpdate.next(url);
  }

  /** Normalize website for API: blank, or absolute URL (adds https when user typed host-only). */
  private normalizeOptionalWebsite(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
  }

  /**
   * Upload any external image URL to our S3 bucket via the backend proxy.
   * The scraper already returns S3 URLs, so this is only needed for the
   * OpenAI image_url fallback (raw third-party URL).
   * Sets productImage to the S3 URL on success, or falls back to the
   * original URL if the upload fails.
   */
  private uploadExternalImageToS3(externalUrl: string): void {
    this.productService.uploadImageFromUrl(externalUrl).pipe(take(1)).subscribe({
      next: (result) => {
        if (result?.s3Url) {
          this.productImage.set(result.s3Url);
        } else {
          // Backend couldn't upload — keep the external URL as fallback
          this.productImage.set(externalUrl);
        }
      },
      error: () => {
        // Network error — keep the external URL as fallback
        this.productImage.set(externalUrl);
      },
    });
  }

  /**
   * Fire scraper + OpenAI in parallel for physical/digital products.
   * Scraper → image (via S3 / SERP fallback on backend, already an S3 URL).
   * OpenAI  → name, description, price, category, industry + image last-resort.
   */
  findProductDetails(url: string): void {
    const brandName = this.currentUserService.focussedBrand.companyName || '';

    // Reset previous results
    this.productImage.set('');

    // 1. Scraper: backend downloads image → uploads to S3 → returns S3 URL
    this.findingProductDetailsScrape.set(true);
    this.productService.getProductDetails(url, brandName).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.findingProductDetailsScrape.set(false);
        if (response?.productImage) {
          // Already an S3 URL — backend handled download + upload
          this.productImage.set(response.productImage);
        }
      },
      error: () => {
        this.findingProductDetailsScrape.set(false);
      },
    });

    // 2. OpenAI: structured metadata extraction
    this.findingProductDetailsOpenAI.set(true);
    this.productService.openAiProductSearch(url).then((response) => {
      this.findingProductDetailsOpenAI.set(false);
      this.physicalProductModel.update(m => ({
        ...m,
        productName: response.product_name || m.productName,
        productDescription: response.description || m.productDescription,
        productValue: response.product_value ?? m.productValue,
        currency: response.product_currency || m.currency,
        productCategory: response.product_category || m.productCategory,
        productIndustry: response.product_industry || m.productIndustry,
      }));
      // Last-resort image: OpenAI found one but scraper found nothing.
      // Route through the backend proxy so we always store our own S3 asset.
      if (!this.productImage() && response.image_url) {
        this.uploadExternalImageToS3(response.image_url);
      }
    }).catch(() => {
      this.findingProductDetailsOpenAI.set(false);
    });
  }

  /**
   * Auto-detect event details from a URL — image from scraper, text from OpenAI.
   */
  findEventDetails(url: string): void {
    const brandName = this.currentUserService.focussedBrand.companyName || '';

    this.productImage.set('');

    this.findingProductDetailsScrape.set(true);
    this.productService.getProductDetails(url, brandName).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.findingProductDetailsScrape.set(false);
        if (response?.productImage) {
          this.productImage.set(response.productImage);
        }
      },
      error: () => { this.findingProductDetailsScrape.set(false); },
    });

    this.findingProductDetailsOpenAI.set(true);
    this.productService.openAiProductSearch(url).then((response) => {
      this.findingProductDetailsOpenAI.set(false);
      this.eventTicketsModel.update(m => ({
        ...m,
        productName: response.product_name || m.productName,
        productDescription: response.description || m.productDescription,
        productValue: response.product_value ?? m.productValue,
        currency: response.product_currency || m.currency,
      }));
      if (!this.productImage() && response.image_url) {
        this.uploadExternalImageToS3(response.image_url);
      }
    }).catch(() => {
      this.findingProductDetailsOpenAI.set(false);
    });
  }

  onImageUploaded(event: { file: string, fileKey: string }) {
    this.productImage.set(event.fileKey);
  }

  onCurrencyChange(event: Event): void {
    const code = (event.target as HTMLSelectElement).value;
    const entry = this.currencies.find((c) => c.code === code);
    this.selectedCurrency.set({
      code,
      symbol: entry?.symbol ?? '',
    });
    this.physicalProductModel.update((m) => ({ ...m, currency: code }));
    this.eventTicketsModel.update((m) => ({ ...m, currency: code }));
  }

  async removeProduct(productId: string) {
    const result = await this.alertService.confirm({
      title: 'Are you sure?',
      message: "You will not be able to use this product on future briefs. Contact support if you've made a mistake.",
      type: 'confirm',
      icon: 'warning',
      cancelText: 'Cancel',
      confirmText: 'Yes, delete it!',
    });
    if (result) {
      this.totallyRemoveProduct(productId);
    }
  }

  async totallyRemoveProduct(productId: string) {
    this.savingProduct.set(true);
    if (this.firebaseService.userId && this.selectedProduct()) {
      try {
        const newProductRes = await firstValueFrom(this.productService.removeProduct(productId));
        this.toastNotificationService.alert({ title: 'Product removed', message: 'Product removed successfully', type: 'success', icon: 'success' });
        this.savingProduct.set(false);
        this.productRemoved.emit(newProductRes);
      } catch (newProductErr) {
        console.error('newProductErr: ', newProductErr);
        this.toastNotificationService.alert({ title: 'Failed to remove product', message: 'Failed to remove product', type: 'error', icon: 'error' });
        this.savingProduct.set(false);
      }
    }
  }

  saveProduct() {
    switch (this.selectedProductType()) {
      case 0:
        this.createPhysicalProduct();
        break;
      case 1:
        this.createPhysicalProduct();
        break;
      case 2:
        this.saveMobileApp();
        break;
      case 3:
        this.saveEventTickets();
        break;
      case 4:
        this.createPhysicalProduct();
        break;
      default:
        break;
    }
  }

  createPhysicalProduct() {
    if (this.physicalProducForm.productName().invalid() || this.physicalProducForm.productDescription().invalid()) {
      this.toastNotificationService.alert({
        title: 'Check your product details',
        message: 'Please fix the highlighted fields before saving.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const website = this.normalizeOptionalWebsite(this.physicalProductModel().website);
    if (website && !this.isValidUrl(website)) {
      this.toastNotificationService.alert({
        title: 'Invalid product link',
        message: 'Leave the link blank or enter a valid URL.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    if (this.isEditMode()) {
      this.savingProduct.set(true);
      const payload = this.buildPhysicalUgcPayload();
      this.productService.editUserProduct(payload).pipe(take(1)).subscribe({
        next: (response) => {
          this.savingProduct.set(false);
          this.productAmended.emit(response);
          this.toastNotificationService.alert({ title: `${this.physicalProductModel().productName} updated`, message: 'Product updated successfully', type: 'success', icon: 'success' });
          this.closePanel();
        },
        error: (err) => {
          console.error('editPhysicalProduct error:', err);
          this.savingProduct.set(false);
          this.toastNotificationService.alert({
            title: 'Failed to save product',
            message: 'Failed to save product',
            type: 'error',
            icon: 'error',
          });
        },
      });
      return;
    }

    this.savingProduct.set(true);
    const newProduct: CreateProductDto = {
      productType: this.selectedProductType(),
      productImage: this.productImage(),
      productName: this.physicalProductModel().productName,
      productDescription: this.physicalProductModel().productDescription,
      productValue: Math.round(this.physicalProductModel().productValue * 100),
      currency: this.physicalProductModel().currency,
      website,
      accessInstructions: this.physicalProductModel().accessInstructions,
      creatorId: this.firebaseService.userId!,
      businessAccountId: this.currentUserService.focussedBrand.id,
      productCategory: this.physicalProductModel().productCategory,
      productIndustry: this.physicalProductModel().productIndustry,
    };

    console.log('👀👀👀 newProduct: ', newProduct);

    this.productService.createUserProduct(newProduct).pipe(take(1)).subscribe({
      next: (response) => {
        this.savingProduct.set(false);
        this.productAdded.emit(response);
        this.closePanel();
      },
      error: (err) => {
        console.error('createPhysicalProduct error:', err);
        this.savingProduct.set(false);
        this.toastNotificationService.alert({
          title: 'Failed to add product',
          message: 'Failed to add product',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  appStoreSearch() {
    if (this.appStoreQuery) {
      this.productService.appStoreSearch(this.appStoreQuery).subscribe({
        next: (appStoreSuggestionRes: any) => {
          this.appleSuggestions.set(appStoreSuggestionRes);
        },
        error: err => {
          console.error('appStoreSearch ERROR: ', err);
        }
      });
    }
  }

  playStoreSearch() {
    if (this.appStoreQuery) {
      this.appStoreSearchLoading.set(true);
      this.playSuggestions.set(undefined);
      this.appleSuggestions.set(undefined);
      this.productService.playStoreSearch(this.appStoreQuery).subscribe({
        next: (playStoreSuggestionRes: any) => {
          this.playSuggestions.set(playStoreSuggestionRes);
          this.appStoreSearchLoading.set(false);
        },
        error: err => {
          console.error('playStoreSearch ERROR: ', err);
          this.appStoreSearchLoading.set(false);
        }
      });
    }
  }

  selectApple(suggested: {
    id: number,
    title: string,
    bundle_id: string,
    seller_link: string,
    description: string,
    link: string,
    screenshots: { link: string, size: string }[],
    logos: { link: string, size: string }[]
  }) {
    this.selectedApple.set(suggested);
    this.appleSuggestions.set(undefined);
  }

  selectPlay(suggested: PlayAppDetailsDto) {
    this.selectedAndroid.set(suggested);
    this.playSuggestions.set(undefined);
  }

  async saveMobileApp() {
    if ((this.selectedApple() || this.selectedAndroid()) && this.firebaseService.userId) {
      const newIosProduct: CreateProductDto = {
        productType: this.selectedProductType(),
        productImage: this.selectedApple() ? this.selectedApple()!.logos[0].link : this.selectedAndroid() ? this.selectedAndroid()!.thumbnail : '',
        productName: this.selectedApple() ? this.selectedApple()!.title : this.selectedAndroid() ? this.selectedAndroid()!.title : '',
        productDescription: this.selectedApple() ? this.selectedApple()!.description : this.selectedAndroid() ? this.selectedAndroid()!.description : '',
        productValue: 0,
        currency: '',
        website: this.selectedApple() ? this.selectedApple()!.seller_link : this.selectedAndroid() ? this.selectedAndroid()!.link : '',
        accessInstructions: this.selectedApple() ? 'Download from AppStore!' : 'Download from PlayStore!',
        creatorId: this.firebaseService.userId!,
        businessAccountId: this.currentUserService.focussedBrand.id,
      };

      if (this.selectedApple()) {
        newIosProduct.iosAppData = this.selectedApple();
      }
      if (this.selectedAndroid()) {
        newIosProduct.androidAppData = this.selectedAndroid();
      }

      if (this.validForm(newIosProduct)) {
        try {
          const newIosProductRes = await firstValueFrom(this.productService.createUserProduct(newIosProduct));
          this.productAdded.emit(newIosProductRes);
          this.toastNotificationService.alert({ title: 'New product added', message: 'New product added successfully', type: 'success', icon: 'success' });
          this.selectedApple.set(undefined);
          this.selectedAndroid.set(undefined);
          this.appStoreQuery = undefined;
          this.closePanel();
        } catch (mobeilAppError) {
          console.error('saveMobileApp error: ', mobeilAppError);
          this.savingProduct.set(false);
          this.toastNotificationService.alert({
            title: 'Whoops!',
            message: 'Please make sure that you complete all the product fields.',
            type: 'error',
            icon: 'error',
          }).then(() => { });
        }
      }
    }
  }

  validForm(newProduct: CreateProductDto): boolean {
    const website = newProduct.website?.trim() ?? '';
    const websiteOk = website === '' || this.isValidUrl(website);
    return !!(
      newProduct.productName.trim() &&
      newProduct.productDescription.trim() &&
      websiteOk &&
      newProduct.productImage
    );
  }

  updateEventTicketsDateType(dateType: 0 | 1) {
    this.eventTicketsModel.update(m => ({ ...m, dateType }));
  }

  getTicketDateTypeStyle(type: 0 | 1) {
    return this.eventTicketsModel().dateType === type
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  onDateChange(event: any) {
    this.selectedDate.set(event);
    this.eventTicketsModel.update(m => ({ ...m, date: event }));
    this.showDateSelect.set(false);
  }

  getDisplayDate(): string {
    const dateValue = this.eventTicketsDate();
    if (!dateValue) return '';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  }

  async saveEventTickets() {
    this.eventTicketsModel.update(m => ({
      ...m,
      date: this.eventTicketsDate() || new Date(),
      currency: this.selectedCurrency()!.code,
    }));

    this.savingProduct.set(true);
    if (this.firebaseService.userId) {
      const website = this.normalizeOptionalWebsite(this.eventTicketsModel().website);
      const newProduct: CreateProductDto = {
        productType: this.selectedProductType(),
        productImage: this.productImage() as string,
        productName: this.eventTicketsModel().productName,
        productValue: Math.round(Number(this.eventTicketsModel().productValue) * 100),
        currency: this.eventTicketsModel().currency,
        productDescription: this.eventTicketsModel().productDescription,
        website,
        accessInstructions: '',
        creatorId: this.firebaseService.userId!,
        businessAccountId: this.currentUserService.focussedBrand.id,
        dateType: this.eventTicketsModel().dateType as 0 | 1,
        date: this.eventTicketsModel().date ? this.eventTicketsModel().date : undefined,
        dateDescription: this.eventTicketsModel().dateDescription || '',
      };

      if (this.validForm(newProduct)) {
        try {
          if (this.isEditMode()) {
            const payload = this.buildEventUgcPayload();
            const updatedRes = await firstValueFrom(this.productService.editUserProduct(payload));
            this.toastNotificationService.alert({ title: 'Event updated', message: 'Event saved successfully', type: 'success', icon: 'success' });
            this.savingProduct.set(false);
            this.productAmended.emit(updatedRes);
            this.closePanel();
          } else {
            const newProductRes = await firstValueFrom(this.productService.createUserProduct(newProduct));
            this.toastNotificationService.alert({ title: 'New event added', message: 'New event added successfully', type: 'success', icon: 'success' });
            this.eventTicketsModel.update(m => ({
              ...m,
              productName: '',
              productDescription: '',
              website: '',
              location: '',
              dateType: 1,
              date: new Date(),
              dateDescription: '',
              productValue: 0,
              currency: this.selectedCurrency()!.code,
            }));
            this.productImage.set('');
            this.savingProduct.set(false);
            this.productAdded.emit(newProductRes);
            this.closePanel();
          }
        } catch (newProductErr) {
          console.error('saveEventTickets error: ', newProductErr);
          this.toastNotificationService.alert({ title: 'Failed to save event', message: 'Failed to save event', type: 'error', icon: 'error' });
          this.savingProduct.set(false);
        }
      } else {
        this.savingProduct.set(false);
        this.alertService.alert({ title: 'Whoops!', message: 'Please make sure that you complete all the event fields.', type: 'alert', icon: 'error', confirmText: 'Okay', cancelText: 'Cancel' });
      }
    }
  }

  closePanel() {
    this.close.emit(true);
  }
}
